<script lang="ts">
    import { tick } from "svelte";
    import { append_empty_stylesheet, onDestroy } from "svelte/internal";

    import { HasID, ListElement, ListElementState, TransitionConfig } from "../animations";

    enum ListEvent {
        Added,
        Removed,
        Kept,
    }

    type ElementType = $$Generic<HasID>;

    export let items: ElementType[] = [];
    export let className: string | undefined;
    export let renderedItems: ListElement<ElementType>[] = [];
    export let transitionIn: ((node: HTMLElement, data: ElementType) => TransitionConfig | null) | null = null;
    export let transitionOut:
        | ((node: HTMLElement, data: ElementType) => (() => TransitionConfig | null) | null)
        | null = null;
    export let transition:
        | ((node: HTMLElement, data: ElementType, swappedPlaces: boolean) => (() => TransitionConfig | null) | null)
        | null = null;

    let prevItems: ElementType[] = [];
    let listElement: HTMLElement;

    let globalAnimationNameIndex = 0;
    const styleSheetNode = append_empty_stylesheet(document.head);
    const stylesheet = styleSheetNode.sheet!;

    $: {
        if (items != prevItems) {
            // Animate the items whenever they change
            prevItems = items;
            setItems(items);
        }
    }

    export function getListElement(): HTMLElement {
        return listElement;
    }

    onDestroy(() => {
        styleSheetNode.remove();
    });

    function diffList<T, U>(
        previousList: T[],
        newList: U[],
        key1: (item: T) => string,
        key2: (item: U) => string,
        migrate: (oldItem: T, newItem: U) => T,
        create: (newItem: U) => T
    ) {
        let previousExists = new Map<string, T>(previousList.map((v) => [key1(v), v]));
        let seen = new Set<string>();

        let added: T[] = [];
        let events: Map<string, ListEvent> = new Map();
        let union: T[] = [];

        for (let item of newList) {
            let itemKey = key2(item);
            seen.add(itemKey);
            const existingItem = previousExists.get(itemKey);
            if (existingItem !== undefined) {
                // Ok
                events.set(itemKey, ListEvent.Kept);
                union.push(migrate(existingItem, item));
            } else {
                const addedItem = create(item);
                added.push(addedItem);
                events.set(itemKey, ListEvent.Added);
                union.push(addedItem);
            }
        }
        for (let item of previousList) {
            let itemKey = key1(item);
            if (!seen.has(itemKey)) {
                events.set(itemKey, ListEvent.Removed);
                union.push(item);
            }
        }

        return { added, events, union };
    }

    let applyingList = false;

    async function setItems(newList: ElementType[]) {
        console.assert(!applyingList);
        applyingList = true;

        const itemsSwapPlaces = new Set<string>();
        let newIDs = renderedItems.map((x) => newList.findIndex((y) => x.data.id == y.id));
        // TODO: Quadratic!
        for (let i = 0; i < newIDs.length; i++) {
            let newID = newIDs[i];
            for (let j = 0; j < i; j++) {
                if (newIDs[j] > newID) {
                    itemsSwapPlaces.add(renderedItems[i].data.id);
                    itemsSwapPlaces.add(renderedItems[j].data.id);
                }
            }
        }

        const { added, events, union } = diffList(
            renderedItems,
            newList,
            (item) => item.data.id,
            (item) => item.id,
            (oldItem, newItem) => {
                oldItem.data = newItem;
                return oldItem;
            },
            (newItem) => {
                return {
                    state: ListElementState.Normal,
                    animation: null,
                    element: null,
                    data: newItem,
                    animationListenerAdded: false,
                };
            }
        );

        // 1. Capture current positions
        // 2. Remove all animations to make items move to the target positions
        // 3. Capture target positions
        // 4. Add animations to move from source to destination

        // Wait until all other svelte changes have been done
        await tick();

        // Run the first part of the transition functions to capture the current state
        let transitions: ((() => TransitionConfig | null) | null)[] = [];
        for (let i = 0; i < union.length; i++) {
            const item = union[i];
            let event = events.get(item.data.id)!;
            let itemTransition: (() => TransitionConfig | null) | null = null;
            if (event == ListEvent.Added) {
                // This item doesn't exist yet. We will add the transition for it later.
                item.state = ListElementState.Normal;
            } else if (event == ListEvent.Removed) {
                item.state = ListElementState.Outro;
                if (transitionOut) itemTransition = transitionOut(item.element!, item.data);
            } else if (event == ListEvent.Kept) {
                item.state = ListElementState.Normal;
                if (transition)
                    itemTransition = transition(item.element!, item.data, itemsSwapPlaces.has(item.data.id));
            }

            transitions.push(itemTransition);
        }

        // Remove the previous animations
        for (let i = 0; i < union.length; i++) {
            const item = union[i];
            // Remove existing animation
            if (item.element != null && item.animation != null) {
                item.element.classList.remove(item.animation + "_static");
            }
            item.animation = null;
        }

        // Remove all existing rules
        for (let i = stylesheet.cssRules.length - 1; i >= 0; i--) {
            stylesheet.deleteRule(i);
        }

        // Note: copy array here because we will modify it while iterating over union later
        renderedItems = Array.from(union);

        // Wait until svelte has applied all DOM changes
        await tick();

        // Add transition-in animations
        for (let i = 0; i < union.length; i++) {
            const item = union[i];
            let event = events.get(item.data.id)!;
            if (event == ListEvent.Added && transitionIn !== null) {
                const f = transitionIn;
                transitions[i] = () => f(item.element!, item.data);
            }
        }

        // Run second stage of transition functions which will capture the state of the elements when
        // they are in the target position. No animations are currently active (we removed them above)
        // so the elements will be laid out the normal way.
        const transitionConfigs = transitions.map((fn) => (fn != null ? fn() : null));

        // Add all the animations to the elements
        for (let i = 0; i < union.length; i++) {
            const item = union[i];
            if (item.element == null) {
                throw "Expected elements to be filled in by this point";
            }

            // Assume the IDs are CSS-safe
            let name = item.data.id + "_animation" + globalAnimationNameIndex;
            globalAnimationNameIndex += 1;
            let static_name = name + "_static";

            if (!item.animationListenerAdded) {
                item.animationListenerAdded = true;
                item.element.addEventListener("animationend", (ev) => {
                    // It's a one-shot animation. We don't need it anymore
                    item.element?.classList.remove(item.animation + "_static");

                    if (ev.animationName == item.animation && item.state == ListElementState.Outro) {
                        // It's better to re-create the array. Svelte keeps the array sometimes and tries to index into it in some callbacks.
                        renderedItems = renderedItems.filter((x) => x !== item);
                    }
                });
            }

            let transition = transitionConfigs[i];

            // It's important that we do not add animations with a zero duration, because they don't seem to fire the 'animationend' callback.
            if (transition !== null && transition.duration > 0) {
                let cssFn = transition.css;
                let duration = transition.duration;
                let delay = transition.delay || 0;

                const keyframesPerSecond = 10;
                const numKeyframes = Math.max(Math.round((duration / 1000) * keyframesPerSecond), 2);
                const ease = transition.easing || ((t) => t);
                let css = `@keyframes ${name} {\n`;
                for (let i = 0; i < numKeyframes; i++) {
                    let t = i / (numKeyframes - 1);
                    const easedT = ease(t);
                    css += `${t * 100}% {${cssFn(easedT, 1 - easedT)}}`;
                }
                if (transition.keyframeHints) {
                    for (let i = 0; i < transition.keyframeHints.length; i++) {
                        let t = Math.min(1, Math.max(0, transition.keyframeHints[i]));
                        const easedT = ease(t);
                        css += `${t * 100}% {${cssFn(easedT, 1 - easedT)}}`;
                    }
                }
                css += "}";

                if (transition.static_css) {
                    console.assert(transition.static_css.trimEnd().endsWith(";"));
                }
                const static_css = `.${static_name} { ${
                    transition.static_css || ""
                } animation: ${name} ${duration}ms linear ${delay}ms 1 both;`;
                stylesheet.insertRule(css, stylesheet.cssRules.length);
                stylesheet.insertRule(static_css, stylesheet.cssRules.length);
                item.element.classList.add(static_name);
                console.assert(item.animation === null);
                item.animation = name;
            } else {
                if (item.state == ListElementState.Outro) {
                    // No animation and the element is going to be removed
                    item.state = ListElementState.PendingDeletion;
                }
                item.animation = null;
            }
        }
        applyingList = false;
        renderedItems = renderedItems.filter((x) => x.state != ListElementState.PendingDeletion);
    }
</script>

<ol class={className} bind:this={listElement}>
    <slot />
</ol>
